import React, { useEffect, useRef, useContext, useState } from "react";
import ReactDOM from "react-dom";
import { uploadImageToS3 } from "../../../../../helpers";
import { DataContext } from "../../../../../context/DataContext";
import { isMobile, isFirefox } from "react-device-detect";
import FirefoxCameraWarning from "../../../../../components/FirefoxCameraWarning";

const YotiFaceCapture = ({
  onReady,
  onLoading,
  onComplete,
  onError,
  language,
}) => {
  const { settings } = useContext(DataContext);
  const rootRef = useRef(null);
  const fcmRef = useRef(null);
  const errorTimerRef = useRef(null);
  const [paddingTop, setPaddingTop] = useState("100px");
  const [viewportSize, setViewportSize] = useState(() => ({
    width: typeof window !== "undefined" ? window.innerWidth : 0,
    height: typeof window !== "undefined" ? window.innerHeight : 0,
  }));
  const [isPreparing, setIsPreparing] = useState(false);
  const [showFirefoxPrompt, setShowFirefoxPrompt] = useState(
    isMobile && isFirefox
  );

  const initializeFaceCapture = () => {
    if (showFirefoxPrompt) return;
    if (window.Yoti && window.Yoti.FaceCaptureModule) {
      console.log("Initializing Yoti face capture");
      const props = {
        faceCaptureAssetsRootUrl: "/assets/face-capture/",
        manualCaptureFallback: false,
        showInitialGuidance: false,
        showGetHelpButton: false,
        encryptImage: false,
        secure: false,
        clientSdkId: "48c6f969-16be-493b-8d7a-8d6f6d7544cc",
        language: language || 'en',
        onSuccess: async (data) => {
          console.log("Yoti face capture successful");
          clearTimeout(errorTimerRef.current);
          onLoading();
          const base64Response = await fetch(
            `data:image/jpeg;base64,${data.img}`
          );
          const blob = await base64Response.blob();
          const suffix = "selfie";
          const imageKey = `${settings.request_id}_dl_${suffix}.jpeg`;
          try {
            const upload = await uploadImageToS3(imageKey, blob, settings);
            console.log("Image uploaded successfully");
            onComplete();
          } catch (error) {
            console.error("Error uploading image:", error);
            onError();
          }
          unmount();
        },
        onError: (error) => {
          console.error("Yoti face capture error:", error);
          clearTimeout(errorTimerRef.current);
          onError();
          unmount();
        },
      };

      try {
        console.log("Rendering Yoti FaceCaptureModule to", rootRef.current);
        fcmRef.current = window.Yoti.FaceCaptureModule.render(
          props,
          rootRef.current
        );
        console.log("Yoti FaceCaptureModule rendered successfully");
        setIsPreparing(false);
        onReady();
      } catch (error) {
        console.error("Error rendering Yoti FaceCaptureModule:", error);
        onError();
      }
    } else {
      console.error("Yoti FaceCaptureModule not found");
      onError();
    }
  };

  const restartFaceCapture = () => {
    if (fcmRef.current) {
      fcmRef.current.unmount();
      fcmRef.current = null;
    }
    setIsPreparing(true);
    setTimeout(() => {
      initializeFaceCapture();
    }, 100);
  };

  // Calculate optimal padding based on viewport height
  useEffect(() => {
    if (!isMobile) {
      // For desktop: dynamically calculate padding to center component vertically
      const calculatePadding = () => {
        const viewportHeight = window.innerHeight;
        // Using 600px as approximate height of the Yoti face capture component
        const componentHeight = 600; 
        
        // For larger screens, use a more balanced calculation
        // For smaller screens, ensure there's still adequate space
        let calculatedPadding;
        
        // Increase padding for all screen sizes
        if (viewportHeight < 700) {
          // For smaller screens, use more padding
          calculatedPadding = Math.max(67, Math.round(viewportHeight * 0.15));
        } else if (viewportHeight < 900) {
          // For medium screens, use more padding
          calculatedPadding = Math.max(67, Math.round(viewportHeight * 0.2));
        } else {
          // For larger screens, use even more padding
          calculatedPadding = Math.max(67, Math.round((viewportHeight - componentHeight) / 2.5));
        }
        
        setPaddingTop(`${calculatedPadding}px`);
        console.log(`Set padding to ${calculatedPadding}px for viewport height ${viewportHeight}px`);
      };
      
      calculatePadding();
      
      // Recalculate on resize
      window.addEventListener('resize', calculatePadding);
      return () => window.removeEventListener('resize', calculatePadding);
    }
  }, []);

  // Handle viewport changes on mobile (orientation/resize)
  useEffect(() => {
    if (isMobile) {
      const updateSize = () =>
        setViewportSize({
          width: window.innerWidth,
          height: window.innerHeight,
        });
      const handleOrientationChange = () => {
        updateSize();
        restartFaceCapture();
      };
      updateSize();
      window.addEventListener("resize", updateSize);
      window.addEventListener("orientationchange", handleOrientationChange);
      return () => {
        window.removeEventListener("resize", updateSize);
        window.removeEventListener("orientationchange", handleOrientationChange);
      };
    }
  }, []);
  
  useEffect(() => {
    if (showFirefoxPrompt) return;
    console.log("YotiFaceCapture component mounted");
    const initTimer = setTimeout(() => {
      initializeFaceCapture();
    }, 100); // Short delay to ensure portal is rendered

    return () => {
      clearTimeout(initTimer);
      if (fcmRef.current) {
        console.log("Unmounting Yoti FaceCaptureModule");
        fcmRef.current.unmount();
      }
    };
  }, [showFirefoxPrompt]);

  // This function is only called after successful capture or explicit error
  const unmount = () => {
    if (fcmRef.current) {
      onReady(false);
      fcmRef.current.unmount();
    }
  };

  // Use React Portal to render the component directly to the document body
  // This completely bypasses any parent container constraints
  // Apply different styles for mobile vs desktop
  const containerStyle = isMobile ?
    {
      position: "fixed",
      top: 0,
      left: 0,
      width: `${viewportSize.width}px`,
      height: `${viewportSize.height}px`,
      zIndex: 9999,
      backgroundColor: "#f5f5f5",
    } : {
      position: "fixed",
      inset: 0,
      paddingTop: paddingTop, // Using dynamically calculated padding
      zIndex: 9999,
      backgroundColor: "rgb(0 0 0)",
    };

  if (showFirefoxPrompt) {
    return (
      <FirefoxCameraWarning onContinue={() => setShowFirefoxPrompt(false)} />
    );
  }

  return ReactDOM.createPortal(
    <>
      {isPreparing && (
        <div
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            width: `${viewportSize.width}px`,
            height: `${viewportSize.height}px`,
            zIndex: 10000,
            backgroundColor: "#f5f5f5",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            fontWeight: 700,
          }}
        >
          Preparing face capture...
        </div>
      )}
      <div
        ref={rootRef}
        id="face-capture-module-root"
        style={containerStyle}
      ></div>
    </>,
    document.body // Render directly to body instead of within parent component
  );
};

export default YotiFaceCapture;
